"use client";

import { cn } from "@/lib/utils";
import { useModal } from "@/providers/modal-provider";
import { HardBreak } from "@tiptap/extension-hard-break";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Ref, useEffect, useImperativeHandle } from "react";
import EditorOptionsMenu from "./components/edit-options-menu";
import { mentionSuggestionOptions } from "./components/suggestion-options";
import "./editor.css";
import { ModalType } from "@/types/modal-type";

type MiniEditorProps = {
	content: string;
	disable?: boolean;
	placeholder?: string;
	disableOptions?: string[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	editorRef?: Ref<any> | null;
	formRef?: React.RefObject<HTMLFormElement>;
	classNames?: { editor?: string; container?: string };
	hideOptions?: boolean;
	disableAllOptions?: boolean;
	handleOnUpdate: ({ content, mentions }: { content: string; mentions?: MentionProp[] }) => void;
};

export type MentionProp = {
	username: string;
	name?: string | null;
};

const MiniEditor = ({
	classNames,
	placeholder,
	content,
	editorRef,
	formRef,
	disable,
	disableOptions,
	disableAllOptions,
	handleOnUpdate,
}: MiniEditorProps) => {
	const { openModal } = useModal();

	const CustomHardBreak = HardBreak.extend({
		addKeyboardShortcuts() {
			return {
				Enter: () => {
					// Submits the form if formRef is provided when clicked enter
					if (formRef && formRef.current) {
						formRef.current.requestSubmit();
					}

					return true;
				},
				"Shift-Enter": () => {
					return this.editor.commands.setHardBreak();
				},
			};
		},
	});

	const extensions = [
		StarterKit,
		CustomHardBreak,
		Link.configure({
			HTMLAttributes: {
				class: "content-link",
			},
			openOnClick: false,
			autolink: true,
		}),
		Placeholder.configure({
			placeholder: placeholder || "Write something",
		}),
		Mention.configure({
			renderHTML({ options, node }) {
				return [
					"a",
					{ href: node.attrs.id ? `/${node.attrs.id}` : `#`, class: "content-mention" },
					`${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`,
				];
			},
			suggestion: mentionSuggestionOptions,
		}),
		Image.configure({
			HTMLAttributes: {
				class: "content-image",
			},
		}),
	];

	const editor = useEditor({
		extensions,
		content: content,
		onUpdate: ({ editor }) => {
			const html = editor.getHTML();
			const json = editor.getJSON();
			const mentions: MentionProp[] = [];

			json?.content?.map((node) => {
				node?.content?.forEach((object) => {
					if (object.type === "mention") {
						const mention = {
							username: object.attrs?.id,
							name: object.attrs?.label,
						};

						mentions.push(mention);
					}
				});
			});

			handleOnUpdate({ content: html, mentions });
		},
		editorProps: {
			attributes: {
				class: cn("mt-4 text-wrap", classNames?.editor),
			},
		},
	});

	const clearEditorContent = () => {
		editor?.chain().clearContent().run();
	};

	useEffect(() => {
		if (disable) {
			editor?.setEditable(false);
		} else {
			editor?.setEditable(true);
		}
	}, [editor, disable]);

	useImperativeHandle(editorRef, () => ({
		clearEditorContent: clearEditorContent,
	}));

	return (
		<div className={cn("w-full", classNames?.container)}>
			{disable && (
				<button
					type="button"
					onClick={() => openModal(ModalType.Auth)}
					className="my-4 text-sm text-gray-400"
				>
					Please login to continue
				</button>
			)}

			{!disableAllOptions && <EditorOptionsMenu editor={editor} disableOptions={disableOptions} />}

			<EditorContent ref={editorRef} editor={editor} className="w-full" />
		</div>
	);
};

export default MiniEditor;
